import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_BASE_URL;
const RESEND_COOLDOWN = 30; // seconds
const MAX_RESENDS = 3;

export default function OtpVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeOtpLogin } = useAuth();

  const otpToken = location.state?.otp_token;
  const emailVerified = location.state?.email_verified;

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [resendCount, setResendCount] = useState(0);
  const [resendMsg, setResendMsg] = useState('');
  const inputRef = useRef(null);

  // If no OTP token, redirect to login
  useEffect(() => {
    if (!otpToken) {
      navigate('/login', { replace: true });
    }
  }, [otpToken, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((c) => c - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Auto-focus OTP input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/verify-otp`,
        { otp },
        { headers: { Authorization: `Bearer ${otpToken}` } },
      );
      // Complete login in AuthContext
      completeOtpLogin(res.data);
      if (!res.data.email_verified) {
        navigate('/email-pending', { replace: true });
      } else {
        navigate('/lung-cancer', { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'OTP verification failed.';
      setError(msg);
      if (msg.includes('expired') || msg.includes('log in again')) {
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendCount >= MAX_RESENDS) return;
    setResendMsg('');
    try {
      const res = await axios.post(
        `${API}/auth/resend-otp`,
        {},
        { headers: { Authorization: `Bearer ${otpToken}` } },
      );
      setResendCount((c) => c + 1);
      setResendCooldown(RESEND_COOLDOWN);
      setResendMsg(`OTP resent. ${res.data.attempts_remaining} resend(s) remaining.`);
      setOtp('');
      setError('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to resend OTP.';
      setResendMsg(msg);
      if (msg.includes('Maximum') || msg.includes('log in again')) {
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(val);
  };

  if (!otpToken) return null;

  return (
    <div className="d-flex align-items-center" style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-sm-10 col-md-8 col-lg-5">
            <div className="text-center mb-4">
              <img
                src={process.env.PUBLIC_URL + '/img/cancepro-logo.png'}
                alt="CancePro"
                width="60"
                height="60"
                className="mb-2"
              />
              <h2 className="fw-bold">Verify Your Identity</h2>
              <p className="text-muted">
                A 6-digit OTP has been sent to your email.
                <br />It expires in <strong>3 minutes</strong>.
              </p>
            </div>

            <div className="card">
              <div className="card-body p-4">
                {error && (
                  <div className="alert alert-danger text-center py-2" style={{ fontSize: '13pt' }}>
                    {error}
                  </div>
                )}
                {resendMsg && (
                  <div className={`alert ${resendMsg.includes('resent') ? 'alert-success' : 'alert-warning'} text-center py-2`}>
                    {resendMsg}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Enter OTP</label>
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control text-center"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="------"
                      maxLength={6}
                      style={{ fontSize: '1.8rem', letterSpacing: '12px', fontWeight: 600 }}
                      required
                      autoComplete="one-time-code"
                    />
                  </div>

                  <div className="d-grid mb-3">
                    <button type="submit" className="btn btn-primary" disabled={loading || otp.length !== 6}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Verifying...
                        </>
                      ) : (
                        'Verify OTP'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center">
                  {resendCount >= MAX_RESENDS ? (
                    <p className="text-danger mb-0">
                      Maximum resend attempts reached. Please <a href="/login">log in again</a>.
                    </p>
                  ) : resendCooldown > 0 ? (
                    <p className="text-muted mb-0">
                      Resend OTP in <strong>{resendCooldown}s</strong>
                    </p>
                  ) : (
                    <button className="btn btn-link p-0" onClick={handleResend}>
                      Resend OTP
                    </button>
                  )}
                </div>

                <div className="text-center mt-3">
                  <a href="/login" className="text-muted small">Back to Login</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
